import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiLink } from 'react-icons/fi';
import styled from 'styled-components';
import { Button, Modal } from 'components';
import { ADMIN } from 'constants/roles';
import { REGION_PAGE_MODULES } from 'constants/routes';
import { useAuthStore } from 'stores/authStore';
import { useMobileAppPromptModalStore } from 'stores/mobileAppPromptModalStore';
import { useUserTourStore } from 'stores/userTourStore';
import { useWhatsNewModalStore } from 'stores/whatsNewModalStore';
import WhatsNewImage from "assets/img/what's-new.webp";

const SEEN_RELEASE = 'seen_release';

export const WHATS_NEW_RELEASE = {
  id: '2026-07-region-page-modules',
  dateLabel: 'July 2026',
  title: 'Build your region page with modules',
  items: [
    {
      audiences: [ADMIN],
      title: 'Create a region page that fits your brand',
      description: 'Build and maintain your customer-facing region page from the admin panel.',
      bullets: [
        'Choose the content type for each module, including looks, favorite products, artists, locations, events, banners, and videos.',
        'Control how many modules appear, then add, remove, and reorder them whenever you need.',
        'Changes are reflected on the customer-facing region website.',
        'Open the Region page manager using the link beside the title, then use Preview in Region settings to review the page as customers will see it.',
      ],
      ctaLabel: 'Manage region page',
      to: REGION_PAGE_MODULES,
    },
  ],
};

export default function WhatsNewModal() {
  const { t } = useTranslation(['app']);
  const role = useAuthStore((state) => state.role);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  const isMobileAppPromptOpen = useMobileAppPromptModalStore((state) => state.isOpen);
  const tourActive = useUserTourStore((state) => state.tourActive);
  const isOpen = useWhatsNewModalStore((state) => state.isOpen);
  const setIsOpen = useWhatsNewModalStore((state) => state.setIsOpen);
  const automaticReleaseId = useRef(null);
  const items = WHATS_NEW_RELEASE.items.filter((item) => item.audiences.includes(role));

  useEffect(() => () => setIsOpen(false), [setIsOpen]);

  useEffect(() => {
    if (isImpersonating) {
      setIsOpen(false);
      return;
    }
    if (
      items.length === 0 ||
      isMobileAppPromptOpen ||
      tourActive ||
      automaticReleaseId.current === WHATS_NEW_RELEASE.id
    ) {
      return;
    }

    automaticReleaseId.current = WHATS_NEW_RELEASE.id;
    if (window.localStorage.getItem(SEEN_RELEASE) !== WHATS_NEW_RELEASE.id) {
      setIsOpen(true);
    }
  }, [isImpersonating, isMobileAppPromptOpen, items.length, setIsOpen, tourActive]);

  const handleClose = () => {
    window.localStorage.setItem(SEEN_RELEASE, WHATS_NEW_RELEASE.id);
    setIsOpen(false);
  };

  if (!isOpen || isImpersonating || items.length === 0) return null;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      closeOnDocumentClick={false}
      header={{ title: `${t('whatsNew.title')} - ${WHATS_NEW_RELEASE.dateLabel}` }}
      bodyStyle={{
        width: 'min(680px, calc(100vw - 48px))',
        minHeight: 0,
        overflowY: 'auto',
      }}
      footer={() => (
        <Footer>
          <Button type="button" variant="primary" className="rounded" onClick={handleClose}>
            {t('whatsNew.gotIt')}
          </Button>
        </Footer>
      )}
    >
      <Content>
        <HeroImage src={WhatsNewImage} alt="Region page modules preview" />
        <TitleRow>
          <ReleaseTitle>{WHATS_NEW_RELEASE.title}</ReleaseTitle>
          {items.map(
            (item) =>
              item.ctaLabel &&
              item.to && (
                <TitleLink
                  key={item.to}
                  to={item.to}
                  onClick={handleClose}
                  aria-label={item.ctaLabel}
                  title={item.ctaLabel}
                >
                  <FiLink aria-hidden="true" />
                </TitleLink>
              ),
          )}
        </TitleRow>
        <Items>
          {items.map((item) => (
            <article key={item.title}>
              <ItemDescription>{item.description}</ItemDescription>
              <BenefitList>
                {item.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </BenefitList>
            </article>
          ))}
        </Items>
      </Content>
    </Modal>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #212529;
  text-align: center;
`;

const HeroImage = styled.img`
  width: 100%;
  aspect-ratio: 2 / 1;
  object-fit: cover;
  object-position: top;
  border-radius: 4px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 24px 0 12px;
`;

const ReleaseTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  line-height: 1.25;
`;

const TitleLink = styled(Link)`
  width: 32px;
  height: 32px;
  display: inline-flex;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  color: #fff;
  background-color: #be1622;
  border-radius: 8px;

  &:hover,
  &:focus {
    color: #fff;
    background-color: #a2131d;
  }
`;

const Items = styled.div`
  width: 100%;
  display: grid;
  gap: 20px;
`;

const ItemDescription = styled.p`
  margin: 0;
  max-width: 560px;
  color: #65757d;
  font-size: 16px;
  line-height: 1.5;
`;

const BenefitList = styled.ul`
  max-width: 560px;
  margin: 18px auto 0;
  padding: 0;
  display: grid;
  gap: 10px;
  color: #495057;
  font-size: 15px;
  line-height: 1.45;
  text-align: left;
  list-style: none;

  li {
    display: grid;
    grid-template-columns: 7px 1fr;
    gap: 10px;
  }

  li::before {
    content: '';
    width: 6px;
    height: 6px;
    margin-top: 8px;
    background-color: #be1622;
    border-radius: 50%;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  padding: 12px 18px 16px;
  border-top: 1px solid #e9ecef;
`;
